import { useQuery } from "@tanstack/react-query";
import { useGetLatestEnterpriseApp } from "controller";
import { generateQRCode } from "utilz/helpers/generateQrCode";

/**
 * Rasterises one download URL into a QR code, cached per URL. This is a local
 * computation rather than a request, so it opts out of the app's network
 * defaults — it must still run offline, and `generateQRCode` resolves with ""
 * instead of rejecting, so retrying it would never help.
 */
const useQrCodeImageUrl = (url?: string) => {
  const { data: qrImageUrl } = useQuery({
    queryKey: ["app-qr-code", url],
    queryFn: () => generateQRCode(url ?? ""),
    enabled: !!url,
    staleTime: Infinity,
    networkMode: "always",
    retry: false,
  });

  return qrImageUrl ?? "";
};

/**
 * QR codes for an enterprise MSP's own white-labelled IT-Connect builds. One
 * response carries both platforms, so the Android and iOS sections share a
 * single request.
 */
export const useEnterpriseAppQrCodes = ({
  mspId,
  enabled,
}: {
  mspId: string;
  enabled: boolean;
}) => {
  const { data, isLoading, isError, isSuccess, refetch } =
    useGetLatestEnterpriseApp({ mspId, enabled });

  const androidQrImageUrl = useQrCodeImageUrl(data?.app_url);
  const iosQrImageUrl = useQrCodeImageUrl(data?.apple_url);

  return {
    androidQrImageUrl,
    iosQrImageUrl,
    isLoading,
    isError,
    refetch,
    /** Absent until the MSP publishes a build for that platform. */
    hasNoAndroidBuild: isSuccess && !data?.app_url,
    hasNoIosBuild: isSuccess && !data?.apple_url,
  };
};
